import { useEffect, useRef, useState } from "react";
import { Fuel } from "lucide-react";
import { HeaderTools } from "@/components/HeaderTools";

export function AppHeader({ title, subtitle, right }) {
  return (
    <header className="app-header" data-testid="app-header">
      <div className="header-logo"><Fuel size={18} color="#030712" strokeWidth={2.6} /></div>
      <div className="header-text">
        <div className="header-title" data-testid="header-title">{title}</div>
        {subtitle && <div className="header-sub">{subtitle}</div>}
      </div>
      <HeaderTools />
      {right}
    </header>
  );
}

export function Screen({ title, subtitle, right, testID, children }) {
  return (
    <div className="screen" data-testid={testID}>
      <AppHeader title={title} subtitle={subtitle} right={right} />
      <div className="screen-body">{children}</div>
    </div>
  );
}

export const Card = ({ testID, className = "", children }) => (
  <section className={`card ${className}`} data-testid={testID}>{children}</section>
);
export const SectionTitle = ({ children }) => <h2 className="section-title">{children}</h2>;
export const Label = ({ children }) => <div className="label">{children}</div>;

export function Chip({ testID, active, onPress, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current, row = el?.parentElement;
    if (active && row && row.scrollWidth > row.clientWidth) row.scrollLeft = el.offsetLeft - row.offsetLeft - (row.clientWidth - el.offsetWidth) / 2;
  }, [active]);
  return (
    <button ref={ref} type="button" className={`chip ${active ? "chip-active" : ""}`} data-testid={testID} onClick={onPress}>
      {children}
    </button>
  );
}

export const ChipRow = ({ testID, children, wrap }) => <div className={`chip-row${wrap ? " chip-row-wrap" : ""}`} data-testid={testID}>{children}</div>;

export function PrimaryButton({ testID, onPress, small, children, variant = "", disabled }) {
  return (
    <button type="button" className={`primary-btn ${small ? "primary-btn-small" : ""} ${variant}`} data-testid={testID} onClick={onPress} disabled={disabled}>
      {children}
    </button>
  );
}

export function IconButton({ testID, onPress, children, label }) {
  return (
    <button type="button" className="header-btn" data-testid={testID} onClick={onPress} aria-label={label}>
      {children}
    </button>
  );
}

const fmt = (v) => (Number.isFinite(v) ? String(Math.round(v * 1000) / 1000) : "");

export function DraftNumber({ testID, value, onValue, decimal, placeholder, blank, className = "" }) {
  const show = (v) => (blank && !v ? "" : fmt(v));
  const [draft, setDraft] = useState(show(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setDraft(show(value)); }, [value, focused]); // eslint-disable-line react-hooks/exhaustive-deps
  const onChange = (e) => {
    const t = (decimal ? e.target.value.replace(/,/g, ".") : e.target.value).replace(decimal ? /[^0-9.]/g : /[^0-9]/g, "").replace(/(\..*)\./g, "$1");
    setDraft(t);
    const n = parseFloat(t);
    if (Number.isFinite(n)) onValue(n);
    else if (t === "") onValue(0);
  };
  return (
    <input
      className={`num-input ${className}`}
      data-testid={testID}
      inputMode={decimal ? "decimal" : "numeric"}
      enterKeyHint="done"
      autoComplete="off"
      value={draft}
      placeholder={placeholder}
      onFocus={(e) => { setFocused(true); e.target.select(); }}
      onBlur={() => { setFocused(false); setDraft(show(value)); }}
      onChange={onChange}
    />
  );
}

export const Field = ({ label, children }) => (
  <label className="field"><Label>{label}</Label>{children}</label>
);
